/**
 * frontend/src/views/UploadView.ts
 * ==================================
 * Vista 1 — Pantalla de carga de archivos.
 *
 * Responsabilidades:
 *   - Renderizar dos FileDropZones (extracto XLSX/CSV + libro mayor XLSX/CSV)
 *   - Mostrar un ProfileSelector bajo cada zona para configurar el mapeo
 *   - Habilitar el botón solo con ambos archivos Y ambos perfiles elegidos
 *   - Llamar a iniciarConciliacion() y transicionar al estado "procesando"
 *   - Mostrar errores de validación inline
 *
 * Las preferencias llegan como getter y no como objeto, a propósito: ver el
 * comentario de `obtenerPreferencias` más abajo.
 */

import { iniciarConciliacion, ApiError } from "../api";
import { FileDropZone } from "../components/FileDropZone";
import { ProfileSelector } from "../components/ProfileSelector";
import type { Preferences } from "../types";
import { problemaAlIniciar } from "../utils/destinatarios";
import { icon } from "../utils/icons";

export function renderUploadView(
  container: HTMLElement,
  /**
   * Getter, no el objeto: se lee al apretar "Iniciar", no al renderizar.
   *
   * El modal de preferencias construye un objeto NUEVO y lo devuelve; quien
   * lo recibe reasigna su variable. Una vista que hubiera capturado el objeto
   * anterior al renderizarse se queda con él para siempre —los archivos ya
   * están cargados, así que la vista no se vuelve a montar— y concilia con
   * las preferencias viejas sin ninguna señal de que eso pasó.
   */
  obtenerPreferencias: () => Preferences,
  onIniciar: (threadId: string) => void,
  /** Abre Preferencias en los destinatarios; `alGuardar` corre si se guardan. */
  abrirDestinatarios: (alGuardar: () => void) => void,
): void {
  container.innerHTML = "";

  // ── Estado local de la vista ──────────────────────────────────────────────
  // El mapeo de columnas elegido vive en cada ProfileSelector y se lee de ahí
  // al iniciar (`selector.seleccion`), así no hay dos fuentes de verdad.
  let extractoFile: File | null = null;
  let libroFile:    File | null = null;
  let loading = false;

  // ── Layout ────────────────────────────────────────────────────────────────
  const wrapper = document.createElement("div");
  wrapper.className = "upload-view";
  wrapper.innerHTML = `
    <ol class="workflow" aria-label="Etapas de conciliación">
      <li class="workflow__step is-active" aria-current="step"><span>01</span> Preparar fuentes</li>
      <li class="workflow__step"><span>02</span> Analizar movimientos</li>
      <li class="workflow__step"><span>03</span> Revisar y aprobar</li>
      <li class="workflow__step"><span>04</span> Recibir reporte</li>
    </ol>

    <div class="upload-view__dropzones" id="dropzones"></div>

    <div class="upload-view__error" id="error-msg" role="alert" hidden></div>

    <div class="upload-view__footer">
    <div class="upload-view__launch">
    <span class="wiz-tip-wrap" id="iniciar-tip" tabindex="0" role="note" data-tooltip="Cargue ambos archivos y seleccione sus perfiles para continuar." aria-label="Cargue ambos archivos y seleccione sus perfiles para continuar.">
    <button
      class="btn btn--primary btn--large"
      id="btn-iniciar"
      type="button"
      disabled
    >
      Iniciar conciliación ${icon("arrow")}
    </button>
    </span>
    </div>

    <p class="upload-view__privacy">
      ${icon("shield")} Los archivos se procesan en memoria y no se almacenan permanentemente.
    </p>
    </div>
  `;
  container.appendChild(wrapper);

  // ── Referencias ───────────────────────────────────────────────────────────
  const dropzonesEl = wrapper.querySelector<HTMLElement>("#dropzones")!;
  const errorEl     = wrapper.querySelector<HTMLElement>("#error-msg")!;
  const iniciarTip  = wrapper.querySelector<HTMLElement>("#iniciar-tip")!;
  const btnIniciar  = wrapper.querySelector<HTMLButtonElement>("#btn-iniciar")!;

  // ── FileDropZone + ProfileSelector: Extracto ──────────────────────────────

  const extractoZone = new FileDropZone({
    label: "Extracto Bancario",
    hint: "XLSX o CSV del extracto bancario.",
    acceptedFormats: [".xlsx", ".xls", ".csv"],
    acceptedMime: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv,application/csv",
    icon: "bank",
    onFile: (file) => {
      extractoFile = file;
      extractoSelector.setArchivo(file);
      clearError();
      updateButton();
    },
    onError: showError,
  });

  const extractoSelector = new ProfileSelector({
    tipoFuente: "extracto",
    onProfileSelected: () => { clearError(); updateButton(); },
  });

  // Wrapper para drop zone + selector
  const extractoWrapper = document.createElement("div");
  extractoWrapper.className = "upload-zone-group";
  extractoWrapper.dataset.recorrido = "extracto";
  extractoWrapper.innerHTML = `<div class="source-card__header"><span class="source-card__number">01</span><h2>Extracto bancario</h2><span class="source-card__type">BANCO</span></div>`;
  extractoWrapper.appendChild(extractoZone.element);
  extractoWrapper.appendChild(extractoSelector.element);
  dropzonesEl.appendChild(extractoWrapper);

  // ── FileDropZone + ProfileSelector: Libro Mayor ───────────────────────────

  const libroZone = new FileDropZone({
    label: "Libro Mayor",
    hint: "XLSX o CSV del Libro Mayor / ERP.",
    acceptedFormats: [".xlsx", ".xls", ".csv"],
    acceptedMime: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv",
    icon: "ledger",
    onFile: (file) => {
      libroFile = file;
      libroSelector.setArchivo(file);
      clearError();
      updateButton();
    },
    onError: showError,
  });

  const libroSelector = new ProfileSelector({
    tipoFuente: "libro_mayor",
    onProfileSelected: () => { clearError(); updateButton(); },
  });

  const libroWrapper = document.createElement("div");
  libroWrapper.className = "upload-zone-group";
  libroWrapper.dataset.recorrido = "libro";
  libroWrapper.innerHTML = `<div class="source-card__header"><span class="source-card__number">02</span><h2>Libro mayor</h2><span class="source-card__type">CONTABILIDAD</span></div>`;
  libroWrapper.appendChild(libroZone.element);
  libroWrapper.appendChild(libroSelector.element);
  dropzonesEl.appendChild(libroWrapper);

  // ── Helpers de UI ─────────────────────────────────────────────────────────

  /**
   * Requisitos para conciliar: los dos archivos y el mapeo de columnas de cada
   * uno. El perfil es obligatorio porque sin él no se puede saber qué cuenta
   * conciliar ni cómo interpretar los importes, y adivinarlo produce
   * resultados verosímiles pero incorrectos.
   */
  function listo(): boolean {
    return Boolean(
      extractoFile && libroFile &&
      extractoSelector.seleccion && libroSelector.seleccion
    );
  }

  function updateButton(): void {
    btnIniciar.disabled = !listo() || loading;
    btnIniciar.innerHTML = loading ? `${icon("spark")} Iniciando…` : `Iniciar conciliación ${icon("arrow")}`;
    const motivo = loading ? "Iniciando conciliación…" : !listo()
      ? "Cargue ambos archivos y seleccione sus perfiles para continuar." : "";
    iniciarTip.dataset.tooltip = motivo;
    if (motivo) {
      iniciarTip.tabIndex = 0;
      iniciarTip.setAttribute("role", "note");
      iniciarTip.setAttribute("aria-label", motivo);
    } else {
      iniciarTip.removeAttribute("tabindex");
      iniciarTip.removeAttribute("aria-label");
      iniciarTip.removeAttribute("role");
    }
  }

  function showError(msg: string): void {
    errorEl.textContent = msg;
    errorEl.hidden = false;
  }

  function clearError(): void {
    errorEl.textContent = "";
    errorEl.hidden = true;
  }

  /** El aviso de los destinatarios, con el acceso directo a corregirlos. */
  function mostrarFaltaDeDestinatarios(problema: string): void {
    showError(problema);
    const ir = document.createElement("button");
    ir.type = "button";
    ir.className = "btn btn--secondary btn--sm upload-view__error-accion";
    ir.textContent = "Indicar destinatarios";
    ir.addEventListener("click", () => abrirDestinatarios(() => {
      if (!problemaAlIniciar(obtenerPreferencias().emailDestinatarios)) clearError();
    }));
    errorEl.appendChild(ir);
    errorEl.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }

  // ── Acción principal ──────────────────────────────────────────────────────
  btnIniciar.addEventListener("click", async () => {
    // La comprobación se repite en vez de delegar en `listo()` para que el
    // compilador pueda estrechar los tipos de los archivos a File.
    if (!extractoFile || !libroFile || !listo() || loading) return;

    // Para conciliar no hacen falta destinatarios —se piden al enviar—, pero
    // una dirección mal escrita se avisa ahora y no al final. Se lee al
    // apretar porque Preferencias se edita aparte.
    const problema = problemaAlIniciar(obtenerPreferencias().emailDestinatarios);
    if (problema) {
      mostrarFaltaDeDestinatarios(problema);
      return;
    }

    loading = true;
    clearError();
    updateButton();

    try {
      const resp = await iniciarConciliacion(
        extractoFile, libroFile, obtenerPreferencias(),
        extractoSelector.seleccion,
        libroSelector.seleccion,
      );
      onIniciar(resp.thread_id);
    } catch (err) {
      // El botón siempre vuelve a estar operable: `iniciarConciliacion` tiene
      // timeout, así que ninguna falla puede dejarlo en "Iniciando…" para siempre.
      loading = false;
      updateButton();
      showError(
        err instanceof ApiError
          ? err.message
          : "No fue posible iniciar la conciliación. Verifique que el servidor esté en funcionamiento."
      );
    }
  });
  updateButton();
}
